import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

function Carousel() {
  const items = [
    {
      id: 'inmoment',
      title: 'The Right Amount of Control',
      subtitle: 'Building a self-serve survey theme and email design system',
      buttonText: 'Explore the toolkit',
    },
    {
      id: 'max',
      title: 'Max',
      subtitle: 'Insights from a site and app redesign',
      buttonText: 'Read the case study',
    },
  ];

  const totalItems = items.length;
  const autoSlideDelay = 7500; // Time between automatic slides
  const autoplayPauseDuration = 10000; // How long manual navigation pauses autoplay
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const pauseTimeoutRef = useRef(null);

  const carouselRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const [currentItem, setCurrentItem] = useState(1);
  // Each item's horizontal position in "container widths": 0 = on screen,
  // 1 = parked off-screen right, -1 = parked off-screen left.
  const [offsets, setOffsets] = useState(() => items.map((_, i) => (i === 1 ? 0 : 1)));
  const [suppressTransition, setSuppressTransition] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  // Set this variable to control visibility of navigation and indicators
  const showNavigationAndIndicators = true; // Change to true to show them

  // Slides `targetIndex` in from the given direction. Two-step so the
  // entering item can be instantly parked on the correct side (no
  // transition) before animating both items to their resting positions -
  // otherwise the browser has nothing to animate *from*. flushSync plus a
  // forced reflow guarantees the "parked" position is actually painted
  // before the transition-triggering update, without relying on
  // requestAnimationFrame timing (unreliable for backgrounded/automated tabs).
  const slideTo = (targetIndex, direction) => {
    // Guard against re-entrancy: a manual click and an autoplay tick (or
    // two rapid clicks) landing back-to-back would otherwise both mutate
    // `offsets` mid-transition and corrupt the animation.
    if (targetIndex === currentItem || isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const entryOffset = direction === 'next' ? 1 : -1;
    const exitOffset = direction === 'next' ? -1 : 1;
    const outgoingIndex = currentItem;

    flushSync(() => {
      setSuppressTransition(true);
      setOffsets((prev) => {
        const next = [...prev];
        next[targetIndex] = entryOffset;
        return next;
      });
    });

    // Force the browser to register the parked position before animating.
    // eslint-disable-next-line no-unused-expressions
    carouselRef.current && carouselRef.current.offsetHeight;

    setSuppressTransition(false);
    setOffsets((prev) => {
      const next = [...prev];
      next[targetIndex] = 0;
      next[outgoingIndex] = exitOffset;
      return next;
    });
    setCurrentItem(targetIndex);

    setTimeout(() => {
      isAnimatingRef.current = false;
    }, 650);
  };

  const pauseAutoplay = () => {
    // Clear the running interval immediately rather than waiting for the
    // `autoplayPaused` state to flow back through the effect below - closes
    // the window where an in-flight autoplay tick could otherwise race a
    // manual click and corrupt the transition.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setAutoplayPaused(true);
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    pauseTimeoutRef.current = setTimeout(() => {
      setAutoplayPaused(false);
    }, autoplayPauseDuration);
  };

  useEffect(() => {
    if (autoplayPaused) {
      return undefined;
    }
    // Autoplay always advances forward (slides in from the right).
    intervalRef.current = setInterval(() => {
      slideTo((currentItem + 1) % totalItems, 'next');
    }, autoSlideDelay);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayPaused, totalItems, currentItem]);

  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  const handleBack = () => {
    pauseAutoplay();
    slideTo((currentItem - 1 + totalItems) % totalItems, 'prev');
  };

  const handleNext = () => {
    pauseAutoplay();
    slideTo((currentItem + 1) % totalItems, 'next');
  };

  const navigateToCaseStudy = (itemId) => {
    navigate(`/case-studies/${itemId}`);
  };

  return (
    <section id="carousel_section">
      <div className="content-container">
        <div className="carousel-container">
          <div className="carousel" ref={carouselRef}>
            {items.map((item, index) => {
              const isActive = index === currentItem;
              return (
                <div
                  key={item.id}
                  id={`carousel-item-${item.id}`}
                  className={`carousel-item ${isActive ? 'active' : ''}`}
                  style={{
                    transform: `translateX(${offsets[index] * 100}%)`,
                    transition: suppressTransition ? 'none' : undefined,
                    zIndex: isActive ? 2 : 1,
                  }}
                >
                  <div className="background-image" />
                  <div className="carousel-content">
                    <h1>{item.title}</h1>
                    <p>{item.subtitle}</p>
                    <button className="button-primary" onClick={() => navigateToCaseStudy(item.id)}>
                      {item.buttonText}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {showNavigationAndIndicators && ( // Conditional rendering
          <>
            <div className="carousel-navigation">
              <button className="back-button" onClick={handleBack}>
                arrow_back_ios_new
              </button>
              <button className="next-button" onClick={handleNext}>
                arrow_forward_ios
              </button>
            </div>
            <div className="carousel-indicators">
              {items.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === currentItem ? 'active' : ''}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default Carousel;
